import { useEffect, useRef, useState } from 'react';

/**
 * Whether an element has been scrolled into view yet.
 *
 * Used to start section animations when they are actually seen. A page that plays every entry
 * animation on mount has already finished animating by the time anyone scrolls to the third
 * section, which is the same as having no animation at all — except for the work.
 *
 * Fires once and then disconnects: a section that re-animates every time it passes the fold is a
 * section nobody can read on the way back up.
 */
export function useInView<T extends HTMLElement>(rootMargin = '-40px') {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const element = ref.current;

    // Without IntersectionObserver — or with reduced motion — everything is simply visible.
    if (!element || typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSeen(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin]);

  return { ref, seen };
}
